import { useState } from 'react';
import { toast } from 'sonner';

import { useRegenerateArtifactDigestMutation } from '@/services/api/artifactDigestService';
import {
  useBulkResortMutation,
  useDestroyBlobMutation,
  useUpdateBlobMutation,
} from '@/services/api/blobService';
import { Blob } from '@/services/types/blob-types';

// Note: Bulk re-sort progress is now displayed in WorkflowIndicator ("Finch is Finching")
// which fetches active operations from the backend. No need for localStorage persistence.

interface UseBulkFileOperationsProps {
  selectedFileIds: Set<string>;
  matterId?: string;
  clearSelection: () => void;
  refetch: () => void;
  setActiveNodeId?: (id: string) => void;
  selectedBlobs?: Blob[];
}

export const useBulkFileOperations = ({
  selectedFileIds,
  matterId,
  clearSelection,
  refetch,
  setActiveNodeId,
  selectedBlobs,
}: UseBulkFileOperationsProps) => {
  // State for bulk operations
  const [isProcessing, setIsProcessing] = useState(false);
  const [isResortLoading, setIsResortLoading] = useState(false);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkRegenerateDialogOpen, setBulkRegenerateDialogOpen] =
    useState(false);
  const [bulkResortDialogOpen, setBulkResortDialogOpen] = useState(false);
  const [bulkOperationError, setBulkOperationError] = useState<string | null>(
    null,
  );
  const [bulkMoveTargetCollectionId, setBulkMoveTargetCollectionId] =
    useState('');

  // Get mutation hooks
  const [updateBlob] = useUpdateBlobMutation();
  const [destroyBlob] = useDestroyBlobMutation();
  const [regenerateArtifactDigest] = useRegenerateArtifactDigestMutation();
  const [bulkResort] = useBulkResortMutation();

  // Open/close dialog handlers
  const openBulkMoveDialog = (initialTargetId: string) => {
    if (!matterId || selectedFileIds.size === 0) return;

    setBulkMoveTargetCollectionId(initialTargetId);
    setBulkMoveDialogOpen(true);
    setBulkOperationError(null);
  };

  const closeBulkMoveDialog = () => {
    setBulkMoveDialogOpen(false);
  };

  const openBulkDeleteDialog = () => {
    if (selectedFileIds.size === 0) return;

    setBulkDeleteDialogOpen(true);
    setBulkOperationError(null);
  };

  const closeBulkDeleteDialog = () => {
    setBulkDeleteDialogOpen(false);
  };

  const openBulkRegenerateDialog = () => {
    if (selectedFileIds.size === 0) return;

    setBulkRegenerateDialogOpen(true);
    setBulkOperationError(null);
  };

  const closeBulkRegenerateDialog = () => {
    setBulkRegenerateDialogOpen(false);
  };

  const openBulkResortDialog = () => {
    if (selectedFileIds.size === 0) return;

    setBulkResortDialogOpen(true);
    setBulkOperationError(null);
  };

  const closeBulkResortDialog = () => {
    setBulkResortDialogOpen(false);
  };

  // Execute bulk move operation
  const executeBulkMove = async () => {
    if (!matterId || selectedFileIds.size === 0) return;

    setIsProcessing(true);
    setBulkOperationError(null);

    try {
      await Promise.all(
        Array.from(selectedFileIds).map((blobId) =>
          updateBlob({
            matterId,
            blobId,
            blob: { id: blobId, collection: bulkMoveTargetCollectionId },
          }).unwrap(),
        ),
      );

      setBulkMoveDialogOpen(false);
      clearSelection();
      setIsProcessing(false);

      // Optionally navigate to the target collection
      if (setActiveNodeId) {
        setActiveNodeId(bulkMoveTargetCollectionId);
      }

      // Refresh the data
      refetch();
    } catch (error) {
      setBulkOperationError('Failed to move files. Please try again.');
      setIsProcessing(false);
    }
  };

  // Execute bulk delete operation
  const executeBulkDelete = async () => {
    if (!matterId || selectedFileIds.size === 0) return;

    setIsProcessing(true);
    setBulkOperationError(null);

    try {
      await Promise.all(
        Array.from(selectedFileIds).map((blobId) =>
          destroyBlob({
            matterId,
            blobId,
          }).unwrap(),
        ),
      );

      setBulkDeleteDialogOpen(false);
      clearSelection();
      setIsProcessing(false);

      // Refresh the data
      refetch();
    } catch (error) {
      setBulkOperationError('Failed to delete files. Please try again.');
      setIsProcessing(false);
    }
  };

  // Execute bulk regenerate operation
  const executeBulkRegenerate = async () => {
    if (selectedFileIds.size === 0) return;

    setIsProcessing(true);
    setBulkOperationError(null);

    try {
      // Filter selected blobs to only those with artifact_id
      const blobsWithArtifacts =
        selectedBlobs?.filter(
          (blob) => selectedFileIds.has(blob.id) && blob.artifact_id,
        ) || [];

      if (blobsWithArtifacts.length === 0) {
        toast.warning('No files with artifacts selected for regeneration');
        setIsProcessing(false);
        return;
      }

      // Regenerate digests for all blobs with artifacts
      await Promise.all(
        blobsWithArtifacts.map((blob) =>
          regenerateArtifactDigest(blob.artifact_id!).unwrap(),
        ),
      );

      const skippedCount = selectedFileIds.size - blobsWithArtifacts.length;

      if (skippedCount > 0) {
        toast.success(
          `Regenerated ${blobsWithArtifacts.length} file(s). ${skippedCount} file(s) skipped (no artifacts).`,
        );
      } else {
        toast.success(
          `Regeneration initiated for ${blobsWithArtifacts.length} file(s)`,
        );
      }

      setBulkRegenerateDialogOpen(false);
      clearSelection();
      setIsProcessing(false);

      // Refresh the data
      refetch();
    } catch (error) {
      setBulkOperationError('Failed to regenerate files. Please try again.');
      setIsProcessing(false);
    }
  };

  // Execute bulk resort operation (async - starts background operation)
  // Progress is shown in WorkflowIndicator which fetches from backend
  const executeBulkResort = async (): Promise<string | null> => {
    if (!matterId || selectedFileIds.size === 0) return null;

    setIsResortLoading(true);
    setBulkOperationError(null);

    try {
      const result = await bulkResort({
        matterId,
        body: { blob_ids: Array.from(selectedFileIds) },
      }).unwrap();

      // Close dialog and clear selection immediately
      setBulkResortDialogOpen(false);
      clearSelection();
      setIsResortLoading(false);

      // Show info toast about async operation
      // Progress is shown in the "Finch is Finching" section (WorkflowIndicator)
      toast.info(`Re-sorting ${result.total} files in the background...`, {
        description:
          'Progress is shown in the "Finch is Finching" indicator above.',
        duration: 4000,
      });

      return result.operation_id;
    } catch (error) {
      setBulkOperationError(
        'Failed to start re-sort operation. Please try again.',
      );
      toast.error('Failed to start re-sort operation. Please try again.');
      setIsResortLoading(false);
      return null;
    }
  };

  return {
    isProcessing,
    isResortLoading,
    bulkMoveDialogOpen,
    bulkDeleteDialogOpen,
    bulkRegenerateDialogOpen,
    bulkResortDialogOpen,
    bulkOperationError,
    bulkMoveTargetCollectionId,
    openBulkMoveDialog,
    openBulkDeleteDialog,
    openBulkRegenerateDialog,
    openBulkResortDialog,
    closeBulkMoveDialog,
    closeBulkDeleteDialog,
    closeBulkRegenerateDialog,
    closeBulkResortDialog,
    executeBulkMove,
    executeBulkDelete,
    executeBulkRegenerate,
    executeBulkResort,
    setBulkMoveTargetCollectionId,
  };
};

export default useBulkFileOperations;
